import {
  autoUpdate,
  FloatingNode,
  FloatingPortal,
  flip,
  offset,
  shift,
  size as sizeMiddleware,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { CaretDownIcon, PlusIcon } from '@phosphor-icons/react'
import React, { useLayoutEffect, useRef, useState } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Chip } from '@/components/ui/chip'
import { ErrorMessage } from '@/components/ui/error-message'
import { Icon } from '@/components/ui/icon'
import { Label } from '@/components/ui/label'
import { QuantityBadge } from '@/components/ui/quantity-badge'

import { DROPDOWN_OFFSET, MAX_VISIBLE_CHIPS } from './chip-select.constants'
import * as S from './chip-select.styled'
import { ChipSelectOption, ChipSelectProps } from './chip-select.types'

export const ChipSelect = ({
  value,
  options,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  error,
  label,
  required,
  readOnly = false,
  maxItems,
  maxItemLength,
  onCreate,
  loading = false,
  chipColor = 'blue',
  size = 'medium',
}: ChipSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const scrollTopRef = useRef(0)
  const restoreScrollRef = useRef(false)
  const nodeId = useFloatingNodeId()

  // Selecting/removing an option reorders (and can resize) the options list, and the
  // field also re-renders when it clears its validation error — either of which would
  // let the browser snap the list back to the top. Capture the scroll position at
  // click time and restore it across the renders that immediately follow, until the
  // next frame (after which the user is free to scroll again).
  useLayoutEffect(() => {
    if (restoreScrollRef.current && listRef.current) {
      listRef.current.scrollTop = scrollTopRef.current
    }
  })

  const preserveScroll = () => {
    if (listRef.current) scrollTopRef.current = listRef.current.scrollTop
    restoreScrollRef.current = true
    requestAnimationFrame(() => {
      restoreScrollRef.current = false
    })
  }

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(DROPDOWN_OFFSET),
      flip(),
      shift({ padding: DROPDOWN_OFFSET }),
      sizeMiddleware({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    nodeId,
    onOpenChange: (open) => {
      setIsOpen(open)
      if (!open) {
        setSearchQuery('')
      }
    },
    open: isOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
  })

  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const { getFloatingProps } = useInteractions([dismiss, role])

  const lowerSearch = searchQuery.toLowerCase().trim()

  const byLockedFirst = (a: ChipSelectOption, b: ChipSelectOption) =>
    a.locked === b.locked ? 0 : a.locked ? -1 : 1

  const selectedOptions = options.filter((o) => value.includes(o.value)).sort(byLockedFirst)
  const unselectedOptions = options.filter((o) => !value.includes(o.value))

  const sortedValue = [...value].sort((a, b) => {
    const aLocked = options.find((o) => o.value === a)?.locked ?? false
    const bLocked = options.find((o) => o.value === b)?.locked ?? false
    return aLocked === bLocked ? 0 : aLocked ? -1 : 1
  })

  const filteredSelected = lowerSearch
    ? selectedOptions.filter((o) => o.label.toLowerCase().includes(lowerSearch))
    : selectedOptions

  const filteredUnselected = lowerSearch
    ? unselectedOptions.filter((o) => o.label.toLowerCase().includes(lowerSearch))
    : unselectedOptions

  const allFiltered: Array<{ option: ChipSelectOption; selected: boolean }> = [
    ...filteredSelected.map((o) => ({ option: o, selected: true })),
    ...filteredUnselected.map((o) => ({ option: o, selected: false })),
  ]

  const hasNoResults = allFiltered.length === 0

  const isAtMax = maxItems !== undefined && value.length >= maxItems

  const canCreate =
    !loading &&
    !!onCreate &&
    !isAtMax &&
    lowerSearch.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === lowerSearch)

  const showBadge = value.length > 0
  const showCaret = !readOnly

  const handleWrapperClick = () => {
    if (disabled || readOnly) return
    inputRef.current?.focus()
    setIsOpen(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (!isOpen) setIsOpen(true)
  }

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      if (options.find((o) => o.value === optionValue)?.locked) return
      preserveScroll()
      onChange(value.filter((v) => v !== optionValue))
    } else {
      if (isAtMax) return
      preserveScroll()
      onChange([...value, optionValue])
    }
  }

  const handleCreate = async () => {
    if (!onCreate || !lowerSearch || isCreating || isAtMax) return
    setIsCreating(true)
    try {
      const created = await onCreate(searchQuery.trim())
      preserveScroll()
      onChange([...value, created])
      setSearchQuery('')
    } finally {
      setIsCreating(false)
    }
  }

  const handleRemove = (optionValue: string) => {
    if (options.find((o) => o.value === optionValue)?.locked) return
    preserveScroll()
    onChange(value.filter((v) => v !== optionValue))
  }

  return (
    <S.Container>
      {label && <Label required={required}>{label}</Label>}

      <S.InputWrapper
        ref={refs.setReference}
        $hasError={!!error}
        $isOpen={isOpen}
        $disabled={disabled}
        $readOnly={readOnly}
        $size={size}
        onClick={handleWrapperClick}
      >
        <S.ChipsAndInput $showBadge={showBadge} $showCaret={showCaret}>
          {sortedValue.slice(0, MAX_VISIBLE_CHIPS).map((v) => {
            const opt = options.find((o) => o.value === v)
            return (
              <Chip
                key={v}
                label={opt?.label ?? v}
                color={opt?.locked ? 'neutral' : chipColor}
                size="small"
                variant="light"
                readOnly={readOnly || opt?.locked}
                onRemove={() => handleRemove(v)}
              />
            )
          })}

          {!readOnly && (
            <>
              {value.length > 0 && <S.TagDivider />}
              <S.InlineInput
                ref={inputRef}
                $size={size}
                disabled={disabled}
                maxLength={maxItemLength}
                placeholder={value.length === 0 ? placeholder : undefined}
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={() => setIsOpen(true)}
              />
            </>
          )}
        </S.ChipsAndInput>

        {(showBadge || showCaret) && (
          <S.RightAdornment>
            {showCaret && (
              <S.CaretIcon $isOpen={isOpen}>
                <Icon icon={CaretDownIcon} size="1.6rem" />
              </S.CaretIcon>
            )}
            {showBadge && <QuantityBadge color="gray" quantity={value.length} size="medium" />}
          </S.RightAdornment>
        )}
      </S.InputWrapper>

      {isOpen && !disabled && !readOnly && (
        <FloatingNode id={nodeId}>
          <FloatingPortal>
            <S.Dropdown ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
              <S.OptionsList
                ref={listRef}
                onScroll={(e) => {
                  if (!restoreScrollRef.current) scrollTopRef.current = e.currentTarget.scrollTop
                }}
              >
                {isAtMax && <S.NoMatch>Maximum {maxItems} tags reached</S.NoMatch>}
                {!isAtMax && hasNoResults && lowerSearch && <S.NoMatch>No match</S.NoMatch>}
                {allFiltered.map(({ option, selected }) => (
                  <S.OptionRow
                    key={option.value}
                    $isSelected={selected}
                    onClick={() => handleToggle(option.value)}
                  >
                    <S.CheckboxWrapper>
                      <Checkbox readOnly checked={selected} size="small" />
                    </S.CheckboxWrapper>
                    <S.OptionLabel>{option.label}</S.OptionLabel>
                  </S.OptionRow>
                ))}
              </S.OptionsList>

              {canCreate && (
                <S.CreateOption onClick={handleCreate}>
                  <Icon icon={PlusIcon} size="1.4rem" />
                  Create &ldquo;{searchQuery.trim()}&rdquo; tag
                </S.CreateOption>
              )}
            </S.Dropdown>
          </FloatingPortal>
        </FloatingNode>
      )}

      <ErrorMessage>
        {error ?? (isAtMax ? `Maximum of ${maxItems} items reached` : null)}
      </ErrorMessage>
    </S.Container>
  )
}
